// Centralized options for Intervention Billing UI

import { MedicationReviewDocTypes } from "../../clinical-services/service-steps/documentation/data";
import { ClinicalServiceTypes } from "../../text/data";

const normalizeServiceKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "");

/** True only when the intervention was created from Annual Medication Review (AMR). */
export const isAnnualMedicationReviewIntervention = (intervention) => {
  const serviceType = intervention?.clinicalServiceType || "";
  const serviceTypeCode = intervention?.clinicalServiceTypeCode || "";
  const amr = ClinicalServiceTypes.MedicationReview;
  return (
    serviceType === amr ||
    serviceTypeCode === amr ||
    normalizeServiceKey(serviceType) === normalizeServiceKey(amr) ||
    normalizeServiceKey(serviceTypeCode) === normalizeServiceKey(amr)
  );
};

/** AMR medication list from fetched clinical service (same path as medications-checklist save). */
export const getAmrMedicationRowsFromClinicalService = (clinicalServiceObj) => {
  const meds =
    clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.medications
      ?.medications;
  return Array.isArray(meds) ? meds : [];
};

/** Medication review row for this intervention: AMR row whose `medicationId` matches the intervention (same key as creation flow). */
export const getAmrMedicationRowForIntervention = (clinicalServiceObj, intervention) => {
  const rows = getAmrMedicationRowsFromClinicalService(clinicalServiceObj);
  const targetId =
    intervention?.medicationId ??
    intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails
      ?.medicationId;
  if (targetId == null || targetId === "" || rows.length === 0) return null;
  const idNorm = String(targetId).toLowerCase().replace(/[{}]/g, "");
  return (
    rows.find((r) => {
      const rid = r?.medicationId ?? r?.MedicationId;
      if (rid == null || rid === "") return false;
      return String(rid).toLowerCase().replace(/[{}]/g, "") === idNorm;
    }) || null
  );
};

export const ResultOptions = [
  { value: "Complete", label: "Complete" },
  { value: "CancelledDeleted", label: "Cancelled / Deleted" },
//   { value: "PharmacistFollowUp", label: "Pharmacist Follow Up" },
];

// Billing PIN options and their paired descriptions
export const BillingPinOptions = [
  {
    value: "93899991",
    label:
      "Change Rx: Update Rx, not filled as prescribed  -  PIN 93899991",
    description:
      "Pending Status Rx not filled as prescribed due to a clinical issue or confirmed as falsified Rx",
  },
  {
    value: "93899992",
    label: "No change to Rx, Continue with original Rx  -  PIN 93899992",
    description:
      "Pharmacist’s recommendation made to prescriber resulting in no change to the prescription; filled as originally prescribed.",
  },
  {
    value: "93899993",
    label: "Change Rx, Change Drug, Dose, Regimen - PIN 93899993",
    description:
      "Pharmacist’s recommendation made to prescriber resulting in a change to the prescription that was subsequently filled as per prescribed change",
  },
];

// Options for Suggestions for Prescriber section (single-select UI)
export const PrescriberSuggestionOptions = [
  { label: "No Change Rx: Continue with original Rx" },
  { label: "Change Rx: Update Rx, not filled as prescribed" },
  { label: "Change Rx: Change Drug, Dose, Regimen" },
];

// Laboratory Recommendations options for PharmacistFollowUp
export const LaboratoryRecommendationOptions = [
  { value: "A1C", label: "A1C" },
  { value: "CBC", label: "CBC (Complete Blood Count)" },
  { value: "CholesterolPanel", label: "Cholesterol Panel" },
  { value: "Electrolytes", label: "Electrolytes (Na, K, Mg)" },
  { value: "FastingPlasmaGlucose", label: "Fasting Plasma Glucose" },
  { value: "INRPT", label: "INR / PT" },
  { value: "LipidPanel", label: "Lipid Panel" },
  { value: "LiverFunctionTests", label: "Liver Function Tests (LFTs)" },
  { value: "SerumCreatinineEGFR", label: "Serum Creatinine + eGFR" },
  { value: "TSH", label: "TSH" },
  { value: "TSHFreeT4", label: "TSH/ Free T4" },
  { value: "UrineAlbuminCreatinineRatio", label: "Urine Alb-to-Creatinine Ratio (ACR)" },
  { value: "VitaminB12Level", label: "Vitamin B12 Level" },
];

// Centralized intervention status enum and helpers
export const InterventionStatus = {
  Active: "Active",
  WaitingProviderResponse: "waitingProviderResponse",
  Completed: "Completed",
  Cancelled: "Cancelled",
};

export const InterventionStatusLabels = {
  [InterventionStatus.Active]: "Active",
  [InterventionStatus.WaitingProviderResponse]: "Waiting Provider Response",
  [InterventionStatus.Completed]: "Completed",
  [InterventionStatus.Cancelled]: "Cancelled / Deleted",
};

/** Active only — tolerates common API casing ("active"). */
export const isActiveInterventionStatus = (status) => {
  if (status == null || status === "") return false;
  const s = String(status).trim();
  return s === InterventionStatus.Active || s.toLowerCase() === "active";
};

/** Waiting provider — enum value is camelCase; some payloads use human-readable labels. */
export const isWaitingProviderInterventionStatus = (status) => {
  if (status == null || status === "") return false;
  const s = String(status).trim();
  if (s === InterventionStatus.WaitingProviderResponse) return true;
  const compact = s.toLowerCase().replace(/\s+/g, "");
  return (
    compact === "waitingproviderresponse" ||
    compact === "waitingprovider" ||
    (compact.includes("waiting") && compact.includes("provider"))
  );
};

/** Active or waiting — used for auto-select and "No Active Interventions" eligibility. */
export const isActiveOrWaitingInterventionStatus = (status) =>
  isActiveInterventionStatus(status) || isWaitingProviderInterventionStatus(status);

/** Newest first, same default as sidebar "Newest on top". */
export const getFirstActiveOrWaitingIntervention = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const eligible = list.filter((item) => isActiveOrWaitingInterventionStatus(item?.status));
  if (eligible.length === 0) return null;
  return [...eligible].sort(
    (a, b) =>
      new Date(b.createdDate || b.lastModifiedDate || 0) -
      new Date(a.createdDate || a.lastModifiedDate || 0)
  )[0];
};

export const getInterventionStatusLabel = (status) => {
  if (!status) return InterventionStatusLabels[InterventionStatus.Active];
  return InterventionStatusLabels[status] || status;
};

/**
 * Completion timestamp: the assessment end date stamped by Billing when the
 * record is completed. Records completed before that stamp existed fall back to
 * the last assessment save, so re-opening an older intervention still shows a date.
 */
export const getInterventionCompletedDate = (intervention) => {
  if (intervention?.status !== InterventionStatus.Completed) return null;
  const formRoot =
    intervention?.interventionAssessment?.interventionAssessmentForm;
  // Legacy records carry no end date; the last assessment save is the closest
  // stored approximation of when Billing completed them.
  return formRoot?.endDateTime || formRoot?.lastModifiedDate || null;
};

/**
 * The pharmacist's wrap-up acknowledgement from the Summary tab. This is a
 * separate fact from the clinical status: Billing already set the record to
 * Completed, and this records that the final review was signed off.
 *
 * Held per browser tab, so it survives navigation and reload but not closing
 * the tab, and it is not visible to other users. Making it durable needs a
 * persisted field on the assessment (see notes on the Summary step).
 */
export const INTERVENTION_COMPLETE_SESSION_PREFIX = "inv-summary-complete-";
export const INTERVENTION_ACKNOWLEDGED_EVENT = "intervention-completion-acknowledged";

export const getInterventionCompleteSessionKey = (interventionId) =>
  interventionId ? `${INTERVENTION_COMPLETE_SESSION_PREFIX}${interventionId}` : null;

/** Ids acknowledged in this tab, read once so rows do not touch storage per render. */
export const readAcknowledgedInterventionIds = () => {
  const ids = new Set();
  if (typeof sessionStorage === "undefined") return ids;
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key && key.startsWith(INTERVENTION_COMPLETE_SESSION_PREFIX)) {
      ids.add(key.slice(INTERVENTION_COMPLETE_SESSION_PREFIX.length));
    }
  }
  return ids;
};

export const isResolvedIntervention = (intervention) => {
  if (!intervention) return false;
  const status = intervention?.status;
  const medicationUpdateComplete =
    intervention?.interventionAssessment?.interventionAssessmentForm
      ?.documentation?.completeMedicationUpdate === true;

  return (
    status === InterventionStatus.Completed ||
    status === InterventionStatus.Cancelled ||
    medicationUpdateComplete
  );
};

// Medication sidebar icon stays through Billing completion and hides only after completeMedicationUpdate (or cancellation).
const isResolvedForMedicationLink = (intervention) => {
  if (!intervention) return false;
  const status = intervention?.status;
  const medicationUpdateComplete =
    intervention?.interventionAssessment?.interventionAssessmentForm
      ?.documentation?.completeMedicationUpdate === true;
  return status === InterventionStatus.Cancelled || medicationUpdateComplete;
};

export const hasActiveInterventionLink = (record, interventions = []) => {
  const normalizeId = (value) =>
    value == null || value === "" ? null : String(value).trim().toLowerCase().replace(/[{}]/g, "");
  const recordId = normalizeId(record?.id);
  const recordInterventionId = normalizeId(record?.interventionId);

  const linkedIntervention = interventions.find(
    (it) => normalizeId(it?.medicationId) === recordId
  );
  if (linkedIntervention) {
    return !isResolvedForMedicationLink(linkedIntervention);
  }

  // Fallback: use embedded intervention data when intervention list is filtered/out-of-sync.
  if (record?.intervention) {
    return !isResolvedForMedicationLink(record.intervention);
  }

  if (!recordInterventionId) return false;

  const recordIntervention = interventions.find(
    (it) => normalizeId(it?.id) === recordInterventionId
  );
  if (recordIntervention) {
    return !isResolvedForMedicationLink(recordIntervention);
  }

  // Last fallback: unresolved interventionId in stale/partial datasets keeps the icon visible.
  return true;
};

// Get status color classes for intervention cards
export const getInterventionStatusColor = (status) => {
  switch (status) {
    case InterventionStatus.Active:
      return "border-green-100 bg-green-50";
    case InterventionStatus.WaitingProviderResponse:
      return "border-yellow-100 bg-yellow-50";
    case InterventionStatus.Completed:
      return "border-gray-100 bg-gray-50";
    case InterventionStatus.Cancelled:
      return "border-red-100 bg-red-50";
    default:
      return "border-gray-100 bg-white";
  }
};

// Documentation form types enum
export const InterventionFormTypes = {
  InterventionDocumentation: "intervention-documentation",
  MedicationRecord: MedicationReviewDocTypes.PersonalMedRecord,
  AMRFollowUp: "amr-follow-up",
  ProviderNotification: MedicationReviewDocTypes.ProviderNotification,
  Notes: "notes",
  PharmaceuticalOpinion: "pharmaceutical-opinion",
  Billing: "billing",
  Adaptation: "adaptation",
  GeneralIntervention: "general-intervention",
};

// Documentation forms list for Intervention Documentation step
// All forms start as disabled, enabled dynamically by components
export const DocumentationForms = [
  {
    id: 1,
    name: "Intervention Documentation Form",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.InterventionDocumentation,
    inActiveIcon: "/icons/intervention/forms/clinicalCommInActive.svg",
    activeIcon: "/icons/intervention/forms/clinicalCommActive.svg",
    // No section = intervention forms
  },
  {
    id: 2,
    name: "Medication Record",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.MedicationRecord,
    inActiveIcon: "/icons/intervention/forms/MedicationRecordInactive.svg",
    activeIcon: "/icons/intervention/forms/MedicationRecordActive.svg",
    section: "AMR"
  },
  {
    id: 3,
    name: "Annual Medication Review Follow-Up  -  Coming Soon",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.AMRFollowUp,
    inActiveIcon: "/icons/intervention/forms/PharmacistFollowupInActive.svg",
    activeIcon: "/icons/intervention/forms/PharmacistFollowupActive.svg",
    section: "AMR"
  },
  {
    id: 4,
    name: "HealthCare Provider Notification of Services",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.ProviderNotification,
    inActiveIcon: "/icons/intervention/forms/NotificationOfServiceInActive.svg",
    activeIcon: "/icons/intervention/forms/NotificationOfServiceActive.svg",
    section: "AMR"
  },
  {
    id: 5,
    name: "Notes",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.Notes,
    inActiveIcon: "/icons/intervention/forms/notesInActive.svg",
    activeIcon: "/icons/intervention/forms/notesActive.svg",
    // No section = intervention forms
  },
  {
    id: 6,
    name: "Pharmaceutical Opinion",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.PharmaceuticalOpinion,
    inActiveIcon: "/icons/intervention/forms/PharmaceuticalOpinionInActive.svg",
    activeIcon: "/icons/intervention/forms/PharmaceuticalOpinionActive.svg",
    // No section = intervention forms
  },
  {
    id: 7,
    name: "Billing",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.Billing,
    inActiveIcon: "/icons/intervention/forms/BilingInActive.svg",
    activeIcon: "/icons/intervention/forms/BilingActive.svg",
    // No section = intervention forms
  },
  {
    id: 8,
    name: "Adaptation",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.Adaptation,
    inActiveIcon: "/icons/intervention/forms/AdaptationInActive.svg",
    activeIcon: "/icons/intervention/forms/AdaptationActive.svg",
    // No section = intervention forms
  },
  {
    id: 9,
    name: "General Intervention",
    enable: false, // Will be enabled by component logic
    type: InterventionFormTypes.GeneralIntervention,
    inActiveIcon: "/icons/intervention/forms/GeneralInterventionInActive.svg",
    activeIcon: "/icons/intervention/forms/GeneralInterventionActive.svg",
    // No section = intervention forms
  },
];

// Enable/disable logic functions for intervention forms
export const checkInterventionFormEnable = (formType, intervention) => {
  // Get intervention assessment form data
  const formRoot = intervention?.interventionAssessment?.interventionAssessmentForm || {};
  const documentation = formRoot?.documentation || {};
  const billing = formRoot?.billing || {};

  const isAMR = isAnnualMedicationReviewIntervention(intervention);

  // In some records `clinicalServiceType` may be missing locally; infer AMR from billing/documentation
  const inferredAMR =
    isAMR ||
    Boolean(billing?.resultAction === "UpdateMedicationRecord") ||
    Boolean(documentation?.completeMedicationUpdate === true);
  
  switch (formType) {
    case InterventionFormTypes.InterventionDocumentation:
      // Always enabled for intervention documentation
      return true;
      
    case InterventionFormTypes.MedicationRecord:
      // Enabled for AMR interventions (or inferred from billing/documentation)
      return inferredAMR;

    case InterventionFormTypes.AMRFollowUp:
      // Enabled for AMR interventions (or inferred from billing/documentation)
      return inferredAMR;

    case InterventionFormTypes.ProviderNotification:
      // Enabled for AMR interventions (or inferred AMR)
      return inferredAMR;

    case InterventionFormTypes.Notes:
      // Always enabled
      return true;

    case InterventionFormTypes.PharmaceuticalOpinion:
      // Enabled if intervention has drug therapy problems in documentation
      return Boolean(documentation?.drugTherapyProblems && documentation.drugTherapyProblems.length > 0);

    case InterventionFormTypes.Billing:
      // Enabled if billing is not complete
      return false;
      
    case InterventionFormTypes.Adaptation:
      // Enabled if prescription adaptation is needed
      return false;
      
    case InterventionFormTypes.GeneralIntervention:
      // Always enabled for general interventions
      return false;
      
    default:
      return false;
  }
};

// Get enabled forms for intervention
export const getEnabledInterventionForms = (intervention) => {
  return DocumentationForms.map(form => ({
    ...form,
    enable: checkInterventionFormEnable(form.type, intervention)
  }));
};

